import L from "leaflet";
import type { LatLng, Route } from "./types";
import { routeSegment, haversine } from "./geo";
import { buildSegmentTimes } from "./live/remainingTime";
import { getMinHours, getMaxHours } from "./dom";

export { routeSegment };

export const iconStart = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#3b82f6;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
export const iconDest = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#ef4444;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// ─── Route context ────────────────────────────────────────────────────────────

export interface RouteContext {
  geometry:     LatLng[];
  durationS:    number;
  totalDistM:   number;
  segmentTimes: number[];   // segmentTimes[i] = duration (s) for geometry[i] → geometry[i+1]
}


// ─── Route drawing ────────────────────────────────────────────────────────────

export function drawRoute(
  route: Route,
  map: L.Map,
): { routeLine: L.Polyline; stopBand: L.FeatureGroup; context: RouteContext } {
  const routeLine = L.polyline(
    route.geometry.map(p => [p.lat, p.lng] as L.LatLngTuple),
    { color: "#22c55e", weight: 3, opacity: 0.8 },
  ).addTo(map);

  const bounds = L.latLngBounds(route.geometry.map(p => [p.lat, p.lng] as L.LatLngTuple));
  map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });

  const totalDistM = route.geometry.slice(1).reduce(
    (s, b, i) => s + haversine(route.geometry[i], b), 0,
  );
  const segmentTimes = buildSegmentTimes(route.geometry, route.steps, route.durationS);
  const stopBand = drawStopBandOnMap(
    map, route.geometry, route.durationS, totalDistM,
  );

  return {
    routeLine,
    stopBand,
    context: { geometry: route.geometry, durationS: route.durationS, totalDistM, segmentTimes },
  };
}

export function drawStopBandOnMap(
  map: L.Map,
  geometry: LatLng[],
  durationS: number,
  totalDistM: number,
): L.FeatureGroup {
  const group = L.featureGroup().addTo(map);

  const minH = getMinHours();
  const maxH = getMaxHours();

  if (maxH <= minH) {
    return group;
  }

  const coords = routeSegment(geometry, durationS, minH * 3600, maxH * 3600);
  if (coords.length < 2) return group;

  // Red dashed: minH → maxH (the stop window only)
  L.polyline(
    coords.map(p => [p.lat, p.lng] as L.LatLngTuple),
    { color: "#ef4444", weight: 4, opacity: 0.85, dashArray: "6, 6" },
  ).addTo(group);

  return group;
}
